import React, { useState, useEffect } from "react";
import {
  Users, ShieldCheck, Bell, Database, Plus, Pencil, Trash2, Check,
  Lock, AlertTriangle, Slack, Loader2, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminUsers } from "@/lib/mock-data";
import { Gated } from "@/components/Gated";
import { format } from "@/lib/brand-tokens";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ADM-15 — System settings
const TABS = [
  { id: "team",         label: "Team & roles",      icon: Users },
  { id: "platform",     label: "Platform",          icon: Database },
  { id: "slack",        label: "Slack",             icon: Slack },
  { id: "notifications",label: "Notifications",     icon: Bell },
  { id: "security",     label: "Security",          icon: ShieldCheck },
];

export default function SettingsPage() {
  const [tab, setTab] = useState("team");

  return (
    <div data-testid="settings-page" className="space-y-5 pb-10">
      <div>
        <div className="eyebrow">System</div>
        <h1 className="font-brand text-display text-ink leading-tight mt-1">Settings</h1>
        <p className="text-body-sm text-ink-muted mt-1">Manage admins, platform defaults, notifications, and security.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* Side nav */}
        <nav className="lg:col-span-3">
          <ul className="bg-surface border border-line rounded-xl overflow-hidden">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <li key={t.id}>
                  <button onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
                          className={`relative w-full text-left px-5 py-3.5 flex items-center gap-3 text-body-sm transition-colors ${
                            tab === t.id
                              ? "bg-brand-50 text-brand-800 font-semibold"
                              : "text-ink-muted hover:bg-surface-alt"
                          }`}>
                    <Icon className={`size-4 ${tab === t.id ? "text-brand-700" : "text-ink-hint"}`} /> {t.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="lg:col-span-9 space-y-5">
          {tab === "team" && <TeamPanel />}
          {tab === "platform" && <PlatformPanel />}
          {tab === "slack" && <SlackPanel />}
          {tab === "notifications" && <NotificationsPanel />}
          {tab === "security" && <SecurityPanel />}
        </div>
      </div>
    </div>
  );
}

function TeamPanel() {
  return (
    <>
      <Panel title="Admin team" subtitle="People with access to the admin portal. Roles control what they can see and do.">
        <div className="flex justify-end mb-4">
          <Gated action="settings.modify">
            <Button className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="invite-admin-btn">
              <Plus className="size-4 mr-1.5" /> Invite admin
            </Button>
          </Gated>
        </div>
        <table className="w-full text-body-sm">
          <thead className="bg-surface-alt">
            <tr className="text-left">
              <th className="px-4 py-3 text-eyebrow uppercase text-ink-hint">Admin</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Role</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">2FA</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Last sign-in</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line/60">
            {adminUsers.map((a) => (
              <tr key={a.id} className="hover:bg-surface-alt/40">
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="size-8 rounded-full bg-ink text-white grid place-items-center text-caption font-semibold">{a.firstName[0]}{a.lastName[0]}</div>
                    <div>
                      <div className="text-ink font-semibold">{a.firstName} {a.lastName}</div>
                      <div className="text-caption text-ink-subtle">{a.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3.5 text-ink-muted">{a.role}</td>
                <td className="px-3 py-3.5">
                  {a.mfaEnabled
                    ? <span className="inline-flex items-center gap-1.5 text-caption text-emerald-700"><Check className="size-3" /> On</span>
                    : <span className="text-caption text-red-700">Off</span>}
                </td>
                <td className="px-3 py-3.5 text-ink-muted text-caption tabular-nums">{format.relative(a.lastSignIn)}</td>
                <td className="px-3 py-3.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Gated action="settings.modify">
                      <button className="size-7 rounded grid place-items-center text-ink-muted hover:bg-surface-alt" data-testid={`edit-admin-${a.id}`}><Pencil className="size-3.5" /></button>
                    </Gated>
                    <Gated action="settings.modify">
                      <button className="size-7 rounded grid place-items-center text-ink-muted hover:bg-red-50 hover:text-red-600" data-testid={`delete-admin-${a.id}`}><Trash2 className="size-3.5" /></button>
                    </Gated>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Roles" subtitle="Five built-in roles cover all admin scenarios. Custom roles are not available in Phase 1.">
        <ul className="divide-y divide-line/60 -mx-2">
          <RoleRow name="Super Admin" body="Full access, including team management and decryption capabilities." />
          <RoleRow name="Deal Manager" body="Create, edit, and publish deals. Approve/reject applications and submissions." />
          <RoleRow name="Payments Overseer" body="Generate payment runs and decrypt bank accounts for export." />
          <RoleRow name="Compliance Reviewer" body="Read-only on members and audit. Can flag accounts for review." />
          <RoleRow name="Read-only Operator" body="View-only. Useful for analysts and engineering observers." />
        </ul>
      </Panel>
    </>
  );
}

function PlatformPanel() {
  return (
    <Panel title="Platform defaults" subtitle="System-wide values. Changes affect newly created entities only — they don&rsquo;t mutate existing records.">
      <Row label="Default rate floor" hint="Deals below this rate require Super Admin approval before publishing.">
        <div className="flex items-center gap-2"><Input className="w-24 tabular-nums" defaultValue="85" /><span className="text-ink-muted">%</span></div>
      </Row>
      <Row label="Default deal cap" hint="Used as the placeholder when creating a new deal.">
        <div className="relative w-40"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-hint">$</span><Input className="pl-7 tabular-nums" defaultValue="10000" /></div>
      </Row>
      <Row label="Default payment terms">
        <Select defaultValue="net7">
          <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="net5">Net 5 days from approval</SelectItem>
            <SelectItem value="net7">Net 7 days from approval</SelectItem>
            <SelectItem value="net10">Net 10 days from approval</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="ACH cutoff" hint="Bank accounts captured after this time on a given day are processed in the next cycle.">
        <Input type="time" className="w-32" defaultValue="14:00" />
      </Row>
      <Row label="Auto-approve cards w/ confidence ≥" hint="Below this, cards land in admin queue for manual triage.">
        <div className="flex items-center gap-2"><Input className="w-24 tabular-nums" defaultValue="98" /><span className="text-ink-muted">%</span></div>
      </Row>
      <div className="pt-3 flex justify-end">
        <Gated action="settings.modify">
          <Button onClick={() => toast.success("Platform settings saved")} className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="save-platform-btn">Save changes</Button>
        </Gated>
      </div>
    </Panel>
  );
}

function NotificationsPanel() {
  return (
    <Panel title="Notification routing" subtitle="Where outbound emails come from and which events trigger admin alerts.">
      <Row label="Outbound from-name"><Input className="w-72" defaultValue="Gift Card Buyers Club" /></Row>
      <Row label="Outbound from-address"><Input className="w-72 font-mono" defaultValue="hello@gcbc.com" /></Row>
      <Row label="Reply-to"><Input className="w-72 font-mono" defaultValue="support@gcbc.com" /></Row>

      <h3 className="text-h4 text-ink font-semibold pt-4">Admin alerts</h3>
      <ToggleRow label="New application submitted" defaultOn />
      <ToggleRow label="Application stuck in queue >48h" defaultOn />
      <ToggleRow label="Card flagged by validation" defaultOn />
      <ToggleRow label="Payment run generated" defaultOn />
      <ToggleRow label="ACH return received" defaultOn />
      <ToggleRow label="Decryption event" hint="Always logged. Toggle controls real-time email." defaultOn />

      <div className="pt-3 flex justify-end">
        <Gated action="settings.modify">
          <Button onClick={() => toast.success("Notification settings saved")} className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="save-notifications-btn">Save changes</Button>
        </Gated>
      </div>
    </Panel>
  );
}

function SecurityPanel() {
  return (
    <>
      <Panel title="Authentication policy">
        <ToggleRow label="Require 2FA for all admins" defaultOn />
        <ToggleRow label="Require 2FA for members" defaultOff />
        <Row label="Lock account after failed attempts" hint="Account is locked for 30 minutes after this many failures.">
          <Input className="w-20 tabular-nums" defaultValue="8" />
        </Row>
        <Row label="Session timeout (idle)" hint="Members are signed out after this many minutes of inactivity.">
          <div className="flex items-center gap-2"><Input className="w-24 tabular-nums" defaultValue="60" /><span className="text-ink-muted">min</span></div>
        </Row>
      </Panel>

      <Panel title="Encryption status">
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-md p-4 flex items-start gap-3">
          <Lock className="size-4 text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <div className="text-body-sm font-semibold text-emerald-900">All sensitive fields encrypted</div>
            <p className="mt-1 text-caption text-ink-muted leading-relaxed">
              KMS key <span className="font-mono text-code">arn:aws:kms:us-east-1:•••:key/4f9a-…</span> · last rotated 18 days ago.
              Card numbers, PINs, and bank accounts are encrypted at rest.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Gated action="settings.modify">
            <Button variant="outline" data-testid="rotate-kms-btn">Rotate KMS key</Button>
          </Gated>
          <Button variant="outline" data-testid="download-encryption-audit-btn">Download encryption audit report</Button>
        </div>
      </Panel>

      <Panel title="Data retention">
        <Row label="Inactive bank account deletion" hint="Bank account for inactive accounts is purged after this many days post-final-payout.">
          <div className="flex items-center gap-2"><Input className="w-24 tabular-nums" defaultValue="90" /><span className="text-ink-muted">days</span></div>
        </Row>
        <Row label="Audit log retention">
          <div className="flex items-center gap-2"><Input className="w-24 tabular-nums" defaultValue="2555" /><span className="text-ink-muted">days (7 years)</span></div>
        </Row>
      </Panel>
    </>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <section className="bg-surface border border-line rounded-xl p-7">
      <h2 className="font-brand text-h2 text-ink">{title}</h2>
      {subtitle && <p className="mt-1 text-caption text-ink-subtle leading-relaxed max-w-2xl">{subtitle}</p>}
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="grid sm:grid-cols-12 gap-3 items-center py-2 border-b border-line/40 last:border-b-0">
      <div className="sm:col-span-5">
        <div className="text-body-sm font-medium text-ink">{label}</div>
        {hint && <div className="text-caption text-ink-subtle leading-relaxed mt-0.5">{hint}</div>}
      </div>
      <div className="sm:col-span-7">{children}</div>
    </div>
  );
}

function ToggleRow({ label, hint, defaultOn = false, defaultOff = false }) {
  const [on, setOn] = useState(defaultOn || !defaultOff);
  return (
    <div className="grid sm:grid-cols-12 gap-3 items-center py-2 border-b border-line/40 last:border-b-0">
      <div className="sm:col-span-9">
        <div className="text-body-sm text-ink">{label}</div>
        {hint && <div className="text-caption text-ink-subtle">{hint}</div>}
      </div>
      <div className="sm:col-span-3 sm:text-right">
        <Switch checked={on} onCheckedChange={setOn} />
      </div>
    </div>
  );
}

function RoleRow({ name, body }) {
  return (
    <li className="px-2 py-3.5">
      <div className="text-body-sm font-semibold text-ink">{name}</div>
      <div className="text-caption text-ink-muted leading-relaxed mt-0.5">{body}</div>
    </li>
  );
}


// ── Slack integration panel ────────────────────────────────────────────────
function SlackPanel() {
  const [status, setStatus] = useState(null);       // { ok, workspace, bot_user_id }
  const [settings, setSettings] = useState(null);   // { enabled, default_channel_id, ... }
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState("");

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [statusRes, settingsRes] = await Promise.all([
        fetch(`${API}/integrations/slack/status`).then(r => r.json()),
        fetch(`${API}/integrations/slack/settings`).then(r => r.json()),
      ]);
      setStatus(statusRes);
      setSettings(settingsRes);
      setSelectedChannel(settingsRes?.default_channel_id || "");
      if (statusRes?.ok) {
        const chRes = await fetch(`${API}/integrations/slack/channels`).then(r => r.json());
        setChannels(chRes.channels || []);
      }
    } catch (e) {
      toast.error("Failed to load Slack settings", { description: String(e) });
    } finally {
      setLoading(false);
    }
  }

  async function saveDefaultChannel() {
    const channel = channels.find(c => c.id === selectedChannel);
    if (!channel) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/integrations/slack/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ default_channel_id: channel.id, default_channel_name: channel.name }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      setSettings(await res.json());
      toast.success(`Default channel set to #${channel.name}`);
    } catch (e) {
      toast.error("Failed to save", { description: String(e.message || e) });
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    setTesting(true);
    try {
      const res = await fetch(`${API}/integrations/slack/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel_id: selectedChannel || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Test failed");
      toast.success(`Test message sent`, { description: `Slack acknowledged ts=${data.ts}` });
      await load();
    } catch (e) {
      toast.error("Test failed", { description: String(e.message || e) });
    } finally {
      setTesting(false);
    }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect Slack? Deal announcements will stop posting.")) return;
    setSaving(true);
    try {
      await fetch(`${API}/integrations/slack/disconnect`, { method: "POST" });
      await load();
      toast.success("Slack disconnected");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Panel title="Slack" subtitle="Loading…">
      <div className="py-8 text-center text-ink-subtle"><Loader2 className="size-5 animate-spin mx-auto" /></div>
    </Panel>;
  }

  const tokenConfigured = settings?.token_configured;
  const connected = !!status?.ok;

  return (
    <Panel
      title="Slack"
      subtitle="Publish deal announcements to a Slack channel automatically (when a deal is published) and on demand from the deal detail page."
    >
      {/* Connection status */}
      <div data-testid="slack-status-card" className={`rounded-lg border p-4 flex items-start gap-3 ${connected ? "bg-emerald-50/40 border-emerald-200" : "bg-amber-50/40 border-amber-200"}`}>
        <Slack className={`size-5 mt-0.5 ${connected ? "text-emerald-700" : "text-amber-700"}`} />
        <div className="flex-1 min-w-0">
          {!tokenConfigured ? (
            <>
              <div className="text-body-sm font-semibold text-ink">No Slack token configured</div>
              <p className="text-caption text-ink-muted mt-1 leading-relaxed">
                Set <code className="font-mono text-[11px] bg-surface-alt px-1 rounded">SLACK_BOT_TOKEN</code> in the backend environment (Bot User OAuth Token starting with <code className="font-mono text-[11px]">xoxb-</code>).
              </p>
            </>
          ) : connected ? (
            <>
              <div className="text-body-sm font-semibold text-ink">Connected to <span className="text-emerald-800">{status.workspace}</span></div>
              <p className="text-caption text-ink-muted mt-1">Bot user ID: <code className="font-mono text-[11px]">{status.bot_user_id}</code></p>
              {status.url && <a href={status.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-caption text-brand-700 hover:text-brand-800 font-semibold">Open workspace <ExternalLink className="size-3" /></a>}
            </>
          ) : (
            <>
              <div className="text-body-sm font-semibold text-ink">Token configured but Slack rejected it</div>
              <p className="text-caption text-amber-800 mt-1">Error: <code className="font-mono text-[11px]">{status?.error || "unknown"}</code> — verify the token is still valid and has <code>chat:write</code> + <code>channels:read</code> scopes.</p>
            </>
          )}
        </div>
        {connected && settings?.enabled && (
          <Button variant="outline" size="sm" onClick={disconnect} disabled={saving} data-testid="slack-disconnect-btn">
            Disconnect
          </Button>
        )}
      </div>

      {/* Channel selector + actions */}
      {connected && (
        <div className="mt-5 pt-5 border-t border-line space-y-4">
          <div>
            <Label className="text-body-sm font-semibold text-ink">Default channel</Label>
            <p className="text-caption text-ink-subtle mt-0.5">Where deal announcements will be posted. Make sure the bot is invited to the channel (<code className="font-mono text-[11px]">/invite @GCBC Deals</code> in Slack).</p>
            <div className="flex items-center gap-2 mt-3">
              <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                <SelectTrigger data-testid="slack-channel-select" className="flex-1 max-w-md">
                  <SelectValue placeholder="Pick a channel…" />
                </SelectTrigger>
                <SelectContent>
                  {channels.length === 0 && <div className="px-3 py-2 text-caption text-ink-subtle">No channels visible — invite the bot to a channel first.</div>}
                  {channels.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.is_private ? "🔒" : "#"} {c.name}
                      {c.is_member && <span className="text-caption text-emerald-700 ml-2">· bot in channel</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={saveDefaultChannel} disabled={saving || !selectedChannel || selectedChannel === settings?.default_channel_id}
                      data-testid="slack-save-btn"
                      className="bg-brand-700 hover:bg-brand-800 text-white">
                {saving ? <Loader2 className="size-4 animate-spin" /> : "Save"}
              </Button>
              <Button variant="outline" onClick={sendTest} disabled={testing || !selectedChannel} data-testid="slack-test-btn">
                {testing ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
                Send test
              </Button>
            </div>
            {settings?.default_channel_name && (
              <p className="text-caption text-ink-muted mt-2">
                Currently saved: <span className="font-mono text-[11px] bg-surface-alt px-1.5 py-0.5 rounded">#{settings.default_channel_name}</span>
                {settings.last_test_at && <span className="ml-2">· Last test {format.relative(settings.last_test_at)}</span>}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Help text */}
      <details className="mt-5 pt-5 border-t border-line">
        <summary className="text-body-sm font-semibold text-ink cursor-pointer">How does auto-publish work?</summary>
        <div className="mt-3 text-caption text-ink-muted leading-relaxed space-y-2">
          <p>When an admin changes a deal&apos;s status from Draft/Review to <span className="font-semibold text-ink">Published</span>, GCBC posts a formatted announcement (deal name, brand list, rate, cap, deadline, Reserve-now button) to the channel above.</p>
          <p>You can also re-post any deal at any time from the deal detail page using the <span className="font-semibold text-ink">Send to Slack</span> button. The bot needs to be a member of the channel before posting; private channels are supported.</p>
        </div>
      </details>
    </Panel>
  );
}
